/**
 * LakshmiDwadashamshaInfoTable Component
 * Displays D12 Lakshmi Dwadashamsha division information with planets that fall in each part
 * Based on AshtamshaInfoTable.tsx but with D12 calculation and Lakshmi qualities
 */

import React from 'react';
import {
    Box,
    Table,
    Thead,
    Tbody,
    Tr,
    Th,
    Td,
    TableContainer,
    Text,
    HStack
} from '@chakra-ui/react';
import {JatakaResponse} from '../../../../../models/api';
import {
    LAKSHMI_DWADASHAMSHA_ATTRIBUTES,
    getLongitudeD12Part,
    getSuryaLongitudeFromApi,
    parseLongitudeFromPosition
} from '../data/LakshmiDwadashamshaTableData';
import {BLACK_ONLY_ASHTAMSHA_COLORS, getAshtamshaPlanetColor} from '../../../../../config/ashtamshaColors';
import {createPlanetDisplay, PlanetDisplay} from '../helpers/planetDisplayHelper';
import {
    TABLE_WRAPPER_STYLES,
    TABLE_CONTAINER_STYLES,
    TABLE_BASE_STYLES,
    TABLE_HEADER_STYLES,
    TABLE_ROW_HOVER_STYLES,
    TABLE_TD_BASE_STYLES,
    PLANET_TEXT_STYLES,
    EMPTY_MESSAGE_BOX_STYLES,
    EMPTY_CELL_TEXT_STYLES,
    COLUMN_STYLES
} from './styles/tableStyles';

export interface LakshmiDwadashamshaInfoTableProps {
    apiResponse?: JatakaResponse | null;
    calculatedData?: {
        planetsByQuality: Record<number, PlanetDisplay[]>;
        suryaReferenceLongitude: number;
        totalPlanets: number;
        qualitiesCount: number;
    };
}

/**
 * Group planets by D12 part they fall into (relative to Surya longitude using global degrees)
 * Now includes Lagna and retrograde indication
 */
function groupPlanetsByD12Part(apiResponse: JatakaResponse): Record<number, PlanetDisplay[]> {
    const planetsByPart: Record<number, PlanetDisplay[]> = {};

    // Initialize all parts with empty arrays
    for (let i = 1; i <= 12; i++) {
        planetsByPart[i] = [];
    }

    // Get Surya longitude for D12 calculation reference (global degrees)
    const suryaLongitude = getSuryaLongitudeFromApi(apiResponse);

    // Get current hora planet for highlighting (same as Graha Chakra)
    const currentHoraPlanet = apiResponse.detailed_panchanga?.hora_details?.current_planet;


    // Process each planet INCLUDING LAGNA
    if (apiResponse.graha_positions?.planets) {
        apiResponse.graha_positions.planets.forEach(planet => {
            // Parse longitude from position (contains global degrees)
            const longitude = parseLongitudeFromPosition(planet.position);

            // Skip if longitude parsing failed
            if (!longitude) return;

            // Calculate relative position from Surya
            const adjustedLongitude = ((longitude - suryaLongitude + 360) % 360);
            // Determine which D12 part this planet falls into (relative to Surya using global degrees)
            const d12Part = getLongitudeD12Part(longitude, suryaLongitude);

            // devLog: Calculate which part of 30 (0-29) and degree within 12° segment (1-12)
            // const partOf30 = Math.floor(adjustedLongitude / 12) + 1; // 1-30
            // const degreeInPart = Math.floor(adjustedLongitude % 12) + 1; // 1-12

            // Use centralized planet display helper (DRY)
            const planetDisplay = createPlanetDisplay(planet, currentHoraPlanet);

            // Add planet to appropriate part
            planetsByPart[d12Part].push(planetDisplay);
        });
    }

    return planetsByPart;
}

export const LakshmiDwadashamshaInfoTable: React.FC<LakshmiDwadashamshaInfoTableProps> = ({apiResponse, calculatedData}) => {
    if (!apiResponse) {
        return (
            <Box {...EMPTY_MESSAGE_BOX_STYLES}>
                <Text>No chart data available</Text>
            </Box>
        );
    }

    // Use calculated data from lazy loading if available, otherwise fallback to synchronous calculation
    const planetsByPart = calculatedData?.planetsByQuality || groupPlanetsByD12Part(apiResponse);

    return (
        <Box {...TABLE_WRAPPER_STYLES}>
            {/* Horizontal scrollable container */}
            <TableContainer {...TABLE_CONTAINER_STYLES}>
                <Table {...TABLE_BASE_STYLES}>
                    <Thead {...TABLE_HEADER_STYLES}>
                        <Tr>
                            <Th {...COLUMN_STYLES.large}>Planets</Th>
                            <Th {...COLUMN_STYLES.medium}>Part</Th>
                            <Th {...COLUMN_STYLES.huge}>Quality</Th>
                            <Th {...COLUMN_STYLES.massive}>Description</Th>
                        </Tr>
                    </Thead>
                    <Tbody>
                        {LAKSHMI_DWADASHAMSHA_ATTRIBUTES.map((division) => {
                            const planetsInPart = planetsByPart[division.part] || [];

                            return (
                                <Tr key={division.part} _hover={TABLE_ROW_HOVER_STYLES}>
                                    <Td {...TABLE_TD_BASE_STYLES} minW="20px">
                                        {planetsInPart.length > 0 ? (
                                            <HStack spacing={1} flexWrap="wrap">
                                                {planetsInPart.map((planet, index) => {
                                                    // Use planetType from helper (DRY)
                                                    const colors = getAshtamshaPlanetColor(planet.planetType, BLACK_ONLY_ASHTAMSHA_COLORS);

                                                    return (
                                                        <Text
                                                            key={`${planet.shortName}-${index}`}
                                                            {...PLANET_TEXT_STYLES}
                                                            color={colors.color}
                                                            bg={colors.backgroundColor}
                                                            border={colors.borderColor ? `1px solid` : 'none'}
                                                            borderColor={colors.borderColor}
                                                        >
                                                            {planet.displayText}
                                                        </Text>
                                                    );
                                                })}
                                            </HStack>
                                        ) : (
                                            <Text {...EMPTY_CELL_TEXT_STYLES}></Text>
                                        )}
                                    </Td>
                                    <Td {...TABLE_TD_BASE_STYLES} textAlign="center" fontWeight="normal">
                                        {division.part}
                                    </Td>
                                    <Td {...TABLE_TD_BASE_STYLES} fontWeight="normal">
                                        {division.quality_english}
                                    </Td>
                                    <Td {...TABLE_TD_BASE_STYLES} fontSize="sm" color="gray.600">
                                        {division.description}
                                    </Td>
                                </Tr>
                            );
                        })}
                    </Tbody>
                </Table>
            </TableContainer>
        </Box>
    );
};